import { Crown, Share2 } from "lucide-react";
import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { EVENT_TYPE_LOUNGE_WIDE } from "@/constants";
import { toast } from "@/hooks/useToast";
import { cn } from "@/lib/utils";
import {
  convertWeekdayToJapanese,
  copyEventLinkToClipboard,
} from "@/lib/utils/eventUtils";

export interface EventCardProps {
  eventCode: string;
  startDate: string;
  startMonth: string;
  startTime: string;
  startWeekday: string;
  description?: string;
  is_draft?: number;
  is_owner?: boolean;
  online_location?: string;
  visibility?: number;
  showVisibilityLabel?: boolean;
  title: string;
  organizer?: string;
  thumbnailUrl?: string;
  isImageLoading?: boolean;
  imageError?: Error | null;
  onClick?: () => void;
  showShareButton?: boolean;
}

export const EventCard: React.FC<EventCardProps> = ({
  eventCode,
  startDate,
  startMonth,
  startTime,
  startWeekday,
  description,
  is_draft,
  is_owner,
  visibility,
  showVisibilityLabel = false,
  title,
  organizer,
  thumbnailUrl,
  isImageLoading = false,
  imageError = null,
  onClick,
  showShareButton = true,
}) => {
  const { t, i18n } = useTranslation();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);

  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageLoadError(false);
  };

  const handleImageError = () => {
    setImageLoadError(true);
    setImageLoaded(false);
  };

  const handleShareClick = async (e: React.MouseEvent) => {
    e.stopPropagation();

    try {
      await copyEventLinkToClipboard(eventCode, t);
      toast({
        title: t("common.success"),
        description: t("common.linkCopied"),
      });
    } catch (error) {
      toast({
        title: t("common.error"),
        description:
          error instanceof Error ? error.message : t("common.linkCopyFailed"),
        variant: "error",
      });
    }
  };

  const stripHtmlTags = (value: string): string => {
    return value
      .replace(/<[^>]*>/g, "")
      .replace(/\s+/g, " ")
      .trim();
  };

  const descriptionText = description ? stripHtmlTags(description) : "";

  const renderImageContent = () => {
    if (imageError) {
      return (
        <div className="flex size-full flex-col items-center justify-center bg-red-50">
          <div className="px-2 text-center text-xs text-red-500">
            <div>Load Error</div>
            <div className="mt-1 text-[10px] opacity-70">API Failed</div>
          </div>
        </div>
      );
    }

    if (isImageLoading) {
      return (
        <div className="flex size-full flex-col items-center justify-center bg-blue-50">
          <div className="mb-1 size-6 animate-spin rounded-full border-2 border-blue-300 border-t-blue-600" />
          <div className="text-xs text-blue-600">{t("common.loading")}</div>
        </div>
      );
    }

    if (thumbnailUrl && !imageLoadError) {
      return (
        <>
          {!imageLoaded && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50">
              <div className="mb-1 size-5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
              <div className="text-xs text-gray-500">{t("common.loading")}</div>
            </div>
          )}
          <img
            src={thumbnailUrl}
            alt="Event thumbnail"
            className={cn(
              "h-full w-full object-cover transition-opacity duration-300",
              imageLoaded ? "opacity-100" : "opacity-0",
            )}
            onLoad={handleImageLoad}
            onError={handleImageError}
          />
        </>
      );
    }

    return null;
  };

  const isJapanese = i18n.language === "ja";
  const organizerLabel = organizer?.trim()
    ? `${t("common.organizer")}: ${organizer.trim()}`
    : "";
  const visibilityLabel =
    visibility === EVENT_TYPE_LOUNGE_WIDE
      ? t("eventManage.loungeWide")
      : t("eventManage.studyGroupOnly");
  const metaLabel =
    organizerLabel || (showVisibilityLabel ? visibilityLabel : "");

  const displayStartWeekday = isJapanese
    ? convertWeekdayToJapanese(startWeekday)
    : startWeekday;

  return (
    <div
      className="flex cursor-pointer items-stretch gap-6 rounded-lg border border-rw-light-gray p-4 transition-shadow hover:shadow-lg"
      onClick={onClick}
    >
      <div className="flex w-[65px] flex-col items-center gap-1 pt-1">
        <div className="text-center">
          <div className="mb-6 text-[16px] font-semibold leading-tight text-rw-dark-gray">
            {isJapanese
              ? `${startMonth}/${startDate}`
              : `${startDate} ${startMonth}`}
          </div>
          {startWeekday && (
            <div className="mt-1 text-[15px] font-semibold text-rw-dark-gray">
              {displayStartWeekday}
            </div>
          )}
          <div className="mt-1 text-[15px] font-semibold text-rw-black-gray">
            {startTime}
          </div>
        </div>
      </div>
      <div className="flex-1">
        {metaLabel ? (
          <div className="mb-3 text-sm text-rw-dark-gray">
            {metaLabel}
            {is_draft === 1 && (
              <span className="ml-2 text-sm font-normal text-orange-600">
                ({t("common.draft")})
              </span>
            )}
          </div>
        ) : null}
        <h3 className="mb-3 text-lg font-bold text-rw-black-gray">
          {is_owner && is_draft !== 1 ? (
            <span
              className="mr-1 inline-flex align-middle"
              title={t("common.creator")}
            >
              <Crown className="size-4 text-amber-600" aria-hidden="true" />
            </span>
          ) : null}
          {title}
        </h3>
        {descriptionText ? (
          <p className="mb-3 line-clamp-2 text-sm text-rw-dark-gray">
            {descriptionText}
          </p>
        ) : null}
      </div>

      <div className="relative h-24 w-32 shrink-0 overflow-hidden rounded">
        {renderImageContent()}
      </div>
      <div className="flex items-center">
        {showShareButton && (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleShareClick}
            aria-label="Share event"
          >
            <Share2 className="size-[18px] text-rw-dark-gray" />
          </Button>
        )}
      </div>
    </div>
  );
};
